// src/pages/CreateStudentFee.jsx
import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import SummaryApi from '../common/index';
import paymentscan from "../assets/paymentscan.jpg";

const CreateStudentFee = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const encodedId = queryParams.get('id');
  const encodedName = queryParams.get('name');

  let student_id = '';
  let student_name = 'Unknown Student';

  try {
    if (encodedId) {
      student_id = atob(encodedId);
    } else {
      throw new Error("No ID in URL");
    }
  } catch (e) {
    console.error("Invalid or missing student ID in URL", e);
    toast.error("Invalid student link. Please use the correct URL.");
  }

  if (encodedName) {
    try {
      student_name = atob(encodedName);
    } catch (e) {
      console.error("Invalid encoded name in URL", e);
      student_name = 'Name not available';
    }
  }
const APPLICATION_FEE = 1000;
  const [formData, setFormData] = useState({
    reference_number: '',
    payment_proof: null,
  });

  const platform_fee = 5;
  const [total, setTotal] = useState(0);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  const proofRef = useRef();
useEffect(() => {
  setTotal(APPLICATION_FEE + platform_fee);
}, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData((prev) => ({ ...prev, payment_proof: file }));
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const validateForm = () => {
    if (!formData.reference_number.trim()) {
      toast.error("Transaction reference / UTR number is required");
      return false;
    }
    if (!formData.payment_proof) {
      toast.error("Payment proof image is required");
      return false;
    }
    if (!student_id) {
      toast.error("Student ID is missing or invalid");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);

    try {
      const adminId = sessionStorage.getItem('admin_id');
      if (!adminId) {
        toast.error("Admin not logged in. Please login again.");
        setLoading(false);
        return;
      }

      const submitData = new FormData();
      submitData.append('student_id', student_id);
submitData.append('application_fee', APPLICATION_FEE);
      submitData.append('platform_fee', platform_fee);
      submitData.append('total_amount', total);
      submitData.append('reference_number', formData.reference_number);
      if (formData.payment_proof) submitData.append('payment_proof', formData.payment_proof);
      submitData.append('admin_id', adminId);
      submitData.append('created_date', new Date().toISOString().slice(0, 19).replace('T', ' '));

      console.log("Submitting to:", SummaryApi.createpaymentstudent.url);
      console.log("FormData contents:");
      for (let [key, val] of submitData.entries()) {
        console.log(
          key,
          val instanceof File
            ? `File → ${val.name} (${(val.size / 1024).toFixed(1)} KB)`
            : val
        );
      }

      const res = await fetch(SummaryApi.createpaymentstudent.url, {
        method: SummaryApi.createpaymentstudent.method,
        body: submitData,
        mode: 'cors',
      });

      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(`Server error ${res.status}: ${errorText || 'No details provided'}`);
      }

      const result = await res.json();

      const isSuccess = result.success === true || result.status === true;

      if (isSuccess) {
        toast.success(result.message || "Payment completed successfully!");
        navigate('/student-report');
      } else {
        toast.error(result.message || result.error || "Payment creation failed – please check details");
      }
    } catch (err) {
      console.error("Submission error:", err);
      const errMsg = err.message || "Unknown error";

      if (errMsg.includes("Failed to fetch")) {
        toast.error("Cannot connect to server – check internet or backend is running");
      } else if (errMsg.includes("CORS")) {
        toast.error("CORS blocked – backend needs to allow this origin");
      } else {
        toast.error(`Error: ${errMsg}`);
      }
    } finally {
      setLoading(false);
    }
  };
const handleBack = () => {
  // Navigate back to registration page with student ID and name
  if (student_id && student_name) {
    const encodedId = btoa(student_id.toString());
    const encodedName = btoa(student_name);
    navigate(`/student-registration?id=${encodedId}&name=${encodedName}`);
  } else {
    // Fallback to dashboard if no student info
    navigate('/dashboard');
  }
};

  const isSubmitDisabled = !formData.reference_number.trim() || !formData.payment_proof || loading;

  const inputClass = "block w-full border border-gray-300 rounded px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none";
  const labelClass = "block text-sm font-medium text-gray-700 mb-1";
  const disabledInputClass = `${inputClass} bg-gray-100 cursor-not-allowed`;

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <div className="bg-white shadow-lg rounded-xl p-6 md:p-8 border border-gray-200 relative">

        {/* Back Button */}
        <button
          onClick={handleBack}
          className="absolute top-6 right-6 px-5 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition flex items-center gap-2 shadow-sm"
        >
          ← Back
        </button>

        <h2 className="text-3xl font-bold text-center text-blue-900 mb-2">
          Student Fee Payment {student_name !== 'Unknown Student' ? `- ${student_name}` : ''}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-12">
          <section>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Left Side: Only QR Code + Reference Number */}
              <div className="space-y-6">
                <h3 className="text-xl font-semibold text-gray-800 mb-5 border-b pb-2">Payment Instructions</h3>
                
                <div>
                  <label className={labelClass}>Scan QR Code for Payment</label>
                  <div className="border border-gray-300 rounded p-4 text-center">
                    <img 
                      src={paymentscan} 
                      alt="Scan QR Code" 
                      className="mx-auto w-64 h-64"
                    />
                    <p className="text-sm text-gray-500 mt-2">Scan this QR code to make payment</p>
                  </div>
                </div>

                <div>
                  <label className={labelClass}>Transaction Reference / UTR No.  <span style={{ color: 'red' }}>*</span></label>
                  <input 
                    name="reference_number" 
                    value={formData.reference_number} 
                    onChange={handleChange} 
                    className={inputClass} 
                    placeholder="Enter UTR / Transaction ID / Reference number" 
                    required 
                  />
                </div>
                         <div className="space-y-3">
          <label className={labelClass}>
  Attach Payment Proof <span style={{ color: 'red' }}>*</span>
</label>
                    <div
                      className="border-2 border-dashed border-gray-300 rounded p-4 text-center cursor-pointer hover:border-blue-500"
                      onClick={() => proofRef.current.click()}
                    >
                      {formData.payment_proof ? (
                        <p className="text-sm text-green-600">Selected: {formData.payment_proof.name}</p>
                      ) : (
                        <p className="text-sm text-gray-500">Click to upload payment proof (jpg/png)</p>
                      )}
                      <input
                        ref={proofRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handleFileChange}
                      />
                    </div>

                    {preview && (
                      <div className="mt-2">
                        <p className="text-sm font-medium text-gray-700 mb-1">Payment Proof Preview:</p>
                        <div className="border border-gray-200 rounded overflow-hidden max-w-xs mx-auto md:mx-0">
                          <img
                            src={preview}
                            alt="Payment proof preview"
                            className="w-full h-auto max-h-48 object-contain"
                          />
                        </div>
                      </div>
                    )}
                  </div>
              </div>

              {/* Right Side: Fee Details + Payment Proof Upload + Preview */}
              <div className="space-y-6">
                <h3 className="text-xl font-semibold text-gray-800 mb-5 border-b pb-2">Fee Details</h3>
                
                <div className="space-y-6">
                  <div className="space-y-4">
                    <div className="flex items-center gap-4">
                      <label className={`${labelClass} w-40 flex-shrink-0`}>Application Fee </label>
    <input 
  value={APPLICATION_FEE} 
  disabled 
  className={`${disabledInputClass} flex-1 font-semibold`} 
/>
                    </div>


                    <div className="flex items-center gap-4">
                      <label className={`${labelClass} w-40 flex-shrink-0`}>Platform Fee</label>
                      <input 
                        value={platform_fee} 
                        disabled 
                        className={`${disabledInputClass} flex-1`} 
                      />
                    </div>

                    <div className="flex items-center gap-4">
                      <label className={`${labelClass} w-40 flex-shrink-0`}>Total Amount</label>
                      <input 
                        value={total} 
                        disabled 
                        className={`${disabledInputClass} flex-1 font-bold`} 
                      />
                    </div>
                  </div>
          <div className="text-center mt-10">
            <button
              type="submit"
              disabled={isSubmitDisabled}
              className={`px-12 py-3 rounded-lg text-white font-semibold text-lg ${
                isSubmitDisabled ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-700 hover:bg-blue-800'
              } transition shadow-md`}
            >
              {loading ? 'Submitting...' : 'Submit Payment'}
            </button>
          </div>
         

  <strong className='text-red-600 text-xs'>Note: <br/>
  Please make sure to attach your payment proof and enter the correct transaction reference / UTR number. Both are required to process your payment successfully.
 </strong> 
                </div>
              </div>
            </div>
          </section>


        </form>
      </div>
    </div>
  );
};

export default CreateStudentFee;